import {
	DeleteOutlined,
	EditOutlined,
	EyeOutlined,
	PrinterOutlined,
	RollbackOutlined,
	SyncOutlined,
	TagOutlined,
	SearchOutlined,
	UserSwitchOutlined,
	ReloadOutlined,
} from "@ant-design/icons";
import { Button, Drawer, Space, Table, Tag, Input, Modal, Select, Tabs, Divider } from "antd";
import ClientForm from "../Client/ClientForm";
import React, { useCallback, useEffect, useState } from "react";
import db from "../../db/database";
import {
	getAllClients,
	getClientById,
	getClientByNo,
} from "../../services/clientService";
import { getColegioByNo } from "../../services/colegioService";
import { getInvoicePaymentLines } from "../../services/invoicePaymentService";
import {
	deleteInvoice,
	getAllInvoices,
	getCompleteInvoice,
	getTodaysInvoicesByType,
	getTodaysPendingInvoices,
	updateInvoice,
} from "../../services/invoiceService";
import { syncInvoiceToBC } from "../../services/invoiceSyncService";
import { getTPVByNoOrId } from "../../services/tpvService";
import "./InvoiceStyles.css";
import InvoiceTicket from "./InvoiceTicket";
import { getAllTurnos } from "../../services/turnoService";

const InvoiceList = ({
	refreshTrigger,
	onEdit,
	processInvoiceForCreditMemo,
	documentType = "Invoice", // Nueva prop para filtrar por tipo de documento ("Invoice" o "Credit Memo")
	syncStatus, // Nueva prop para filtrar por estado de sincronización ("pending")
	dateFilter, // Nueva prop para filtrar por fecha ("today")
}) => {
	const [invoices, setInvoices] = useState([]);
	const [filteredInvoices, setFilteredInvoices] = useState([]);
	const [searchText, setSearchText] = useState("");
	const [loading, setLoading] = useState(false);
	// Estado para controlar el loading por factura individual
	const [syncingInvoices, setSyncingInvoices] = useState(new Set());
	const [selectedInvoice, setSelectedInvoice] = useState(null);
	const [colegio, setColegio] = useState(null);
	const [paymentLines, setPaymentLines] = useState([]);
	const [detailVisible, setDetailVisible] = useState(false);
	const [ticketModalVisible, setTicketModalVisible] = useState(false);
	const [selectedInvoiceForTicket, setSelectedInvoiceForTicket] =
		useState(null);
	const [selectedClientForTicket, setSelectedClientForTicket] = useState(null);
	const [selectedTPVForTicket, setSelectedTPVForTicket] = useState(null);
	const [clients, setClients] = useState([]);
	const [turnos, setTurnos] = useState(null);
	// Reasignación de cliente
	const [reassignModalVisible, setReassignModalVisible] = useState(false);
	const [invoiceToReassign, setInvoiceToReassign] = useState(null);
	const [reassignActiveTab, setReassignActiveTab] = useState("select");
	const [selectedClientIdForReassign, setSelectedClientIdForReassign] = useState(null);
	const [manualClientNoForReassign, setManualClientNoForReassign] = useState("");

	// Cargar clientes al montar el componente
	useEffect(() => {
		const loadClients = async () => {
			try {
				const clientList = await getAllClients();
				setClients(clientList);
			} catch (error) {
				console.error("Error al cargar clientes:", error);
			}
		};
		loadClients();
	}, []);

	useEffect(() => {
		const loadTurnos = async () => {
			const turnos = await getAllTurnos();
			setTurnos(turnos);
		};
		loadTurnos();
	}, []);

	// Determinar si estamos en modo facturas o abonos
	const isInvoiceMode = documentType === "Invoice";
	const isPendingMode = syncStatus === "pending";

	// Función para resetear el estado de sincronización
	const handleResetSyncStatus = async (invoiceId) => {
		const confirmed = window.confirm(
			"¿Estás seguro de que quieres resetear el estado de sincronización?\n\nEsto permitirá intentar sincronizar la factura nuevamente."
		);

		if (!confirmed) return;

		try {
			await db.invoices.update(invoiceId, {
				syncing: false,
				syncStartTime: null
			});

			setSyncingInvoices(prev => {
				const newSet = new Set(prev);
				newSet.delete(invoiceId);
				return newSet;
			});

			window.messageApi.success("Estado de sincronización reseteado");
			await loadInvoices();
		} catch (error) {
			console.error("Error al resetear estado:", error);
			window.messageApi.error("No se pudo resetear el estado");
		}
	};

	const loadInvoices = useCallback(async () => {
		try {
			setLoading(true);

			let filteredInvoices = [];

			// Calcular los modos dentro del callback para evitar dependencias
			const isPending = syncStatus === "pending";
			const isTodayFilter = dateFilter === "today";

			// Si es filtro de hoy, usar las funciones específicas optimizadas
			if (isTodayFilter) {
				if (isPending) {
					filteredInvoices = await getTodaysPendingInvoices();
				} else {
					filteredInvoices = await getTodaysInvoicesByType(documentType);
				}
			} else {
				// Usar el método tradicional para todos los datos
				const invoiceList = await getAllInvoices();

				// Filtrar por estado de sincronización si está especificado
				if (isPending) {
					filteredInvoices = invoiceList.filter(
						(invoice) => !invoice.No && !invoice.deleted && !invoice.isDraft
					);
				} else {
					// Filtrar por tipo de documento
					filteredInvoices = invoiceList.filter(
						(invoice) => invoice.Document_Type === documentType
					);
				}
			}

			setInvoices(filteredInvoices);
		} catch (error) {
			console.error("Error al cargar facturas:", error);
			const isPending = syncStatus === "pending";
			const isInvoice = documentType === "Invoice";
			const errorMessage = isPending
				? "las facturas pendientes de sincronizar"
				: isInvoice
					? "las facturas"
					: "los abonos";
			window.messageApi.error(`No se pudieron cargar ${errorMessage}`);
		} finally {
			setLoading(false);
		}
	}, [documentType, syncStatus, dateFilter]);

	useEffect(() => {
		loadInvoices();
	}, [refreshTrigger, loadInvoices]);

	// Detectar si la factura referencia a un cliente inexistente (ni por id ni por No)
	const isClientMissingForInvoice = useCallback((invoice) => {
		try {
			if (!invoice) return false;
			let existsById = false;
			if (invoice.clientLocalId !== undefined && invoice.clientLocalId !== null) {
				existsById = clients.some((c) => c.id === invoice.clientLocalId);
			}
			let existsByNo = false;
			const sellTo = invoice.Sell_to_Customer_No_;
			if (sellTo !== undefined && sellTo !== null && sellTo !== "null" && sellTo !== "") {
				if (typeof sellTo === "number") {
					existsByNo = clients.some((c) => c.id === sellTo || c.No === String(sellTo));
				} else {
					existsByNo = clients.some((c) => c.No === sellTo);
				}
			}
			return !(existsById || existsByNo);
		} catch (e) {
			return false;
		}
	}, [clients]);

	const openReassignClientModal = (invoice) => {
		setInvoiceToReassign(invoice);
		setSelectedClientIdForReassign(null);
		setManualClientNoForReassign("");
		setReassignActiveTab("select");
		setReassignModalVisible(true);
	};

	const handleConfirmReassignClient = async () => {
		try {
			if (!invoiceToReassign) {
				window.messageApi.warning("No hay factura seleccionada");
				return;
			}

			let updatePayload = null;

			if (reassignActiveTab === "manual") {
				if (!manualClientNoForReassign) {
					window.messageApi.warning("Introduzca el Nº de cliente de Business Central");
					return;
				}
				updatePayload = {
					Sell_to_Customer_No_: manualClientNoForReassign,
					clientLocalId: null,
				};
			} else {
				if (!selectedClientIdForReassign) {
					window.messageApi.warning("Seleccione o cree un cliente");
					return;
				}
				const selectedClient = clients.find((c) => c.id === selectedClientIdForReassign);
				if (!selectedClient) {
					window.messageApi.error("Cliente seleccionado no encontrado");
					return;
				}

				// Regla: si el cliente NO tiene No (BC), usar SIEMPRE el ID local también en Sell_to_Customer_No_
				// para mantener compatibilidad con el resto del flujo (búsquedas, sync y renderizado)
				if (selectedClient.No) {
					updatePayload = {
						Sell_to_Customer_No_: selectedClient.No,
						clientLocalId: null,
					};
				} else {
					updatePayload = {
						Sell_to_Customer_No_: selectedClient.id,
						clientLocalId: selectedClient.id,
					};
				}
			}
			setLoading(true);
			await updateInvoice(invoiceToReassign.id, updatePayload);
			window.messageApi.success("Cliente reasignado correctamente");
			setReassignModalVisible(false);
			setInvoiceToReassign(null);
			setSelectedClientIdForReassign(null);
			setManualClientNoForReassign("");
			await loadInvoices();
		} catch (error) {
			console.error("Error al reasignar cliente:", error);
			window.messageApi.error(error.message || "No se pudo reasignar el cliente");
		} finally {
			setLoading(false);
		}
	};

	// Función para filtrar facturas por texto de búsqueda
	const filterInvoicesBySearch = useCallback(
		(invoiceList, searchValue) => {
			if (!searchValue) return invoiceList;

			const searchLower = searchValue.toLowerCase();
			return invoiceList.filter((invoice) => {
				// Obtener información del cliente
				const clientInfo = clients.find(
					(client) =>
						client.No === invoice.Sell_to_Customer_No_ ||
						client.id === invoice.clientLocalId
				);

				// Obtener información del turno
				const turnoInfo = turnos?.find((t) => t.No === invoice.turno);

				// Campos a buscar
				const searchFields = [
					invoice.id?.toString(),
					invoice.No,
					invoice.Document_Type === "Invoice"
						? "factura"
						: invoice.Document_Type === "Credit Memo"
							? "nota de crédito"
							: invoice.Document_Type,
					invoice.Sell_to_Customer_No_,
					clientInfo?.No,
					invoice.Bill_to_Name,
					invoice.Posting_Date,
					invoice.caja,
					invoice.turno,
					turnoInfo?.DescripcionTurno,
					invoice.Cupon,
					invoice.Tipo_Detalle,
					invoice.No_Detalle,
					invoice.Campaign_No_,
					invoice.isDraft ? "borrador" : "",
					invoice.No ? "sincronizada" : "pendiente",
					invoice.deleted ? "eliminada" : "",
				];

				return searchFields.some(
					(field) =>
						field && field.toString().toLowerCase().includes(searchLower)
				);
			});
		},
		[clients, turnos]
	);

	// Efecto para filtrar facturas cuando cambia el texto de búsqueda
	useEffect(() => {
		const filtered = filterInvoicesBySearch(invoices, searchText);
		setFilteredInvoices(filtered);
	}, [invoices, searchText, filterInvoicesBySearch]);

	const handleEdit = (invoice) => {
		if (!invoice.isDraft) {
			window.messageApi.warning(
				"Solo se pueden editar facturas en estado borrador"
			);
			return;
		}
		onEdit(invoice);
	};

	const handleDelete = async (invoice) => {
		if (!invoice.isDraft) {
			window.messageApi.warning(
				"Solo se pueden eliminar facturas en estado borrador"
			);
			return;
		}

		if (
			window.confirm(
				"¿Está seguro de eliminar esta factura? Esta acción no se puede deshacer"
			)
		) {
			try {
				setLoading(true);
				await deleteInvoice(invoice.id);
				window.messageApi.success("Factura eliminada correctamente");
				await loadInvoices();
			} catch (error) {
				console.error("Error al eliminar factura:", error);
				window.messageApi.error(
					error.message || "No se pudo eliminar la factura"
				);
			} finally {
				setLoading(false);
			}
		}
	};

	const handleSync = async (invoice) => {
		// Verificar si ya se está sincronizando esta factura
		if (syncingInvoices.has(invoice.id)) {
			window.messageApi.warning("Esta factura ya se está sincronizando");
			return;
		}

		// Verificar estado del borrador
		if (invoice.isDraft) {
			window.messageApi.warning(
				"No se pueden sincronizar facturas en estado borrador"
			);
			return;
		}

		// Verificar si ya está sincronizada
		if (invoice.No) {
			window.messageApi.warning("Esta factura ya está sincronizada");
			return;
		}

		// Verificar si está eliminada
		if (invoice.deleted) {
			window.messageApi.warning("Esta factura ha sido eliminada");
			return;
		}

		// Marcar como sincronizando INMEDIATAMENTE - tanto en estado como en BD
		setSyncingInvoices(prev => new Set(prev).add(invoice.id));

		try {
			// Verificar estado actualizado de la factura desde la BD y marcar como sincronizando
			const { getInvoiceById } = await import("../../services/invoiceService");
			const currentInvoice = await getInvoiceById(invoice.id);

			// Verificar nuevamente el estado actual
			if (!currentInvoice) {
				window.messageApi.error("La factura no existe");
				return;
			}

			if (currentInvoice.isDraft) {
				window.messageApi.warning("La factura está en estado borrador");
				return;
			}

			if (currentInvoice.No) {
				window.messageApi.warning("La factura ya está sincronizada");
				return;
			}

			if (currentInvoice.deleted) {
				window.messageApi.warning("La factura ha sido eliminada");
				return;
			}

			// Verificar si ya está marcada como sincronizando en la BD
			if (currentInvoice.syncing) {
				window.messageApi.warning("Esta factura ya se está sincronizando");
				return;
			}

			// Marcar como sincronizando en la BD INMEDIATAMENTE
			await db.invoices.update(invoice.id, {
				syncing: true,
				syncStartTime: new Date().toISOString()
			});

			window.messageApi.info("Sincronizando factura...");

			const result = await syncInvoiceToBC(currentInvoice);

			if (result.success) {
				window.messageApi.success("Factura sincronizada correctamente");
				loadInvoices();
			} else {
				window.messageApi.error(`Error al sincronizar: ${result.message}`);
			}
		} catch (error) {
			console.error("Error al sincronizar factura:", error);
			window.messageApi.error("No se pudo sincronizar la factura");
		} finally {
			// Limpiar marca de sincronizando
			try {
				await db.invoices.update(invoice.id, {
					syncing: false,
					syncStartTime: null
				});
			} catch (dbError) {
				console.error("Error al limpiar estado de sincronización:", dbError);
			}

			// Remover de la lista de sincronizando
			setSyncingInvoices(prev => {
				const newSet = new Set(prev);
				newSet.delete(invoice.id);
				return newSet;
			});
		}
	};

	const handleCreateCreditMemo = (invoice) => {
		if (invoice.isDraft) {
			window.messageApi.warning(
				"No se pueden crear notas de crédito sobre facturas en estado borrador"
			);
			return;
		}

		if (!invoice.No) {
			window.messageApi.warning(
				"No se pueden crear notas de crédito sobre facturas no sincronizadas"
			);
			return;
		}

		// Llamar a la función processInvoiceNo del componente padre
		processInvoiceForCreditMemo(invoice.No);
	};

	const handleViewDetail = async (invoice) => {
		try {
			setLoading(true);
			const completeInvoice = await getCompleteInvoice(invoice.id);
			setSelectedInvoice(completeInvoice);

			// Cargar líneas de pago
			const paymentLinesList = await getInvoicePaymentLines(invoice.id);
			setPaymentLines(paymentLinesList);

			// Cargar información del colegio si existe
			if (completeInvoice.Colegio) {
				try {
					const colegioData = await getColegioByNo(completeInvoice.Colegio);
					setColegio(colegioData);
				} catch (error) {
					console.error("Error al cargar información del colegio:", error);
					setColegio(null);
				}
			} else {
				setColegio(null);
			}

			setDetailVisible(true);
		} catch (error) {
			console.error("Error al obtener detalle de factura:", error);
			window.messageApi.error("No se pudo obtener el detalle de la factura");
		} finally {
			setLoading(false);
		}
	};

	const handlePrintTicket = async (invoice) => {
		try {
			setLoading(true);
			const completeInvoice = await getCompleteInvoice(invoice.id);
			const paymentLines = await getInvoicePaymentLines(invoice.id);

			// Obtener información del cliente
			let clientInfo = null;
			try {
				// Intentar obtener por ID local primero (si existe clientLocalId)
				if (completeInvoice.clientLocalId) {
					clientInfo = await getClientById(completeInvoice.clientLocalId);
				}
				// Si no se encontró por ID local, intentar por No de cliente
				else if (completeInvoice.Sell_to_Customer_No_) {
					clientInfo = await getClientByNo(
						completeInvoice.Sell_to_Customer_No_
					);
				}
			} catch (error) {
				console.warn("No se pudo obtener información del cliente:", error);
			}

			// Obtener información del TPV basado en la caja de la factura
			let tpvInfo = null;
			try {
				if (completeInvoice.caja) {
					// Necesitamos obtener el TPV de la caja
					// Primero obtenemos todas las cajas para encontrar el TPV
					const { getAllCajas } = await import("../../services/cajaService");
					const cajas = await getAllCajas();
					const cajaInfo = cajas.find(c => c.No === completeInvoice.caja);

					if (cajaInfo && cajaInfo.TPV) {
						tpvInfo = await getTPVByNoOrId(cajaInfo.TPV);
					}
				}
			} catch (error) {
				console.warn("No se pudo obtener información del TPV:", error);
			}

			// Incluir las líneas de pago, cliente y TPV en el ticket
			setSelectedInvoiceForTicket({
				...completeInvoice,
				paymentLines: paymentLines,
			});
			setSelectedClientForTicket(clientInfo);
			setSelectedTPVForTicket(tpvInfo);
			setTicketModalVisible(true);
		} catch (error) {
			console.error("Error al preparar ticket para impresión:", error);
			window.messageApi.error("No se pudo preparar el ticket para impresión");
		} finally {
			setLoading(false);
		}
	};

	const getSyncStatusTag = (invoice) => {
		if (invoice.deleted) {
			return <Tag color="red">Eliminada</Tag>;
		}
		if (invoice.isDraft) {
			return <Tag color="orange">BORRADOR</Tag>;
		}
		if (invoice.pendingSync) {
			return <Tag color="orange">Pendiente</Tag>;
		}
		if (invoice.lastSynced) {
			return <Tag color="green">Sincronizada</Tag>;
		}
		return <Tag color="default">Sin sincronizar</Tag>;
	};

	const getInvoiceTypeTag = (type) => {
		if (type === "Invoice") {
			return <Tag color="blue">Factura</Tag>;
		}
		if (type === "Credit Memo") {
			return <Tag color="purple">Nota de crédito</Tag>;
		}
		return <Tag color="default">{type}</Tag>;
	};

	// Función para obtener filtros únicos de una columna
	const getUniqueFilters = (dataIndex, render) => {
		const uniqueValues = new Set();
		filteredInvoices.forEach((invoice) => {
			let value = invoice[dataIndex];
			if (render) {
				value = render(value, invoice);
			}
			if (
				value !== undefined &&
				value !== null &&
				value !== "" &&
				value !== "-"
			) {
				uniqueValues.add(value);
			}
		});
		return Array.from(uniqueValues).map((value) => ({
			text: value,
			value: value,
		}));
	};

	// Función para obtener filtros de clientes únicos
	const getClientFilters = () => {
		const uniqueClients = new Set();
		filteredInvoices.forEach((invoice) => {
			if (
				invoice.Sell_to_Customer_No_ &&
				invoice.Sell_to_Customer_No_ !== "null"
			) {
				const client = clients.find(
					(client) =>
						client.No === invoice.Sell_to_Customer_No_ ||
						client.id === invoice.clientLocalId
				);
				if (client) {
					uniqueClients.add(client.No);
				} else {
					uniqueClients.add(invoice.Sell_to_Customer_No_);
				}
			}
		});
		return Array.from(uniqueClients).map((clientNo) => ({
			text: clientNo,
			value: clientNo,
		}));
	};

	// Función para obtener filtros de turnos únicos
	const getTurnoFilters = () => {
		const uniqueTurnos = new Set();
		filteredInvoices.forEach((invoice) => {
			if (invoice.turno && turnos) {
				const turnoData = turnos.find((t) => t.No === invoice.turno);
				if (turnoData) {
					uniqueTurnos.add(turnoData.DescripcionTurno);
				}
			}
		});
		return Array.from(uniqueTurnos).map((turnoDesc) => ({
			text: turnoDesc,
			value: turnoDesc,
		}));
	};

	// Función para obtener filtros de estado de sincronización
	const getSyncStatusFilters = () => {
		return [
			{ text: "BORRADOR", value: "draft" },
			{ text: "Sincronizada", value: "synced" },
			{ text: "Pendiente", value: "pending" },
			{ text: "Eliminada", value: "deleted" },
		];
	};

	const columns = [
		{
			title: "ID",
			dataIndex: "id",
			key: "id",
			filters: getUniqueFilters("id"),
			filterSearch: true,
			onFilter: (value, record) => record.id?.toString() === value,
			sorter: (a, b) => a.id - b.id,
		},
		{
			title: "Nº",
			dataIndex: "No",
			key: "No",
			render: (text) => text || "Pendiente",
			filters: getUniqueFilters("No", (text) => text || "Pendiente"),
			filterSearch: true,
			onFilter: (value, record) => {
				const displayValue = record.No || "Pendiente";
				return displayValue === value;
			},
			sorter: (a, b) => (a.No || "").localeCompare(b.No || ""),
		},
		{
			title: "Tipo",
			dataIndex: "Document_Type",
			key: "Document_Type",
			render: (text) => {
				return text === "Invoice"
					? "Factura"
					: text === "Credit Memo"
						? "Nota de crédito"
						: text;
			},
		},
		{
			title: "Cliente",
			dataIndex: "Sell_to_Customer_No_",
			key: "Sell_to_Customer_No_",
			render: (text, record) => {
				if (text === undefined || text === null || text === "null") return "-";
				try {
					const client = clients.find(
						(c) => c.No === text || c.id === record.clientLocalId || c.id === text
					);
					// Mostrar No si existe; si no, mostrar ID local
					if (client) return client.No || client.id;
					return text;
				} catch (error) {
					return text;
				}
			},
			filters: getClientFilters(),
			filterSearch: true,
			onFilter: (value, record) => {
				const client = clients.find(
					(client) =>
						client.No === record.Sell_to_Customer_No_ ||
						client.id === record.clientLocalId
				);
				return client
					? client.No === value
					: record.Sell_to_Customer_No_ === value;
			},
			sorter: (a, b) =>
				(a.Sell_to_Customer_No_ || "").localeCompare(
					b.Sell_to_Customer_No_ || ""
				),
		},
		{
			title: "Nombre",
			dataIndex: "Bill_to_Name",
			key: "Bill_to_Name",
			filters: getUniqueFilters("Bill_to_Name"),
			filterSearch: true,
			onFilter: (value, record) => record.Bill_to_Name === value,
			sorter: (a, b) =>
				(a.Bill_to_Name || "").localeCompare(b.Bill_to_Name || ""),
		},
		{
			title: "Fecha",
			dataIndex: "Posting_Date",
			key: "Posting_Date",
			render: (date) => {
				if (!date) return "-";
				return new Date(date).toISOString().split("T")[0];
			},
			filters: getUniqueFilters("Posting_Date", (date) => {
				if (!date) return "-";
				return new Date(date).toISOString().split("T")[0];
			}),
			filterSearch: true,
			onFilter: (value, record) => {
				const displayDate = record.Posting_Date
					? new Date(record.Posting_Date).toISOString().split("T")[0]
					: "-";
				return displayDate === value;
			},
			sorter: (a, b) =>
				new Date(a.Posting_Date || 0) - new Date(b.Posting_Date || 0),
		},
		{
			title: "Caja",
			dataIndex: "caja",
			key: "caja",
			render: (caja) => caja || "-",
			filters: getUniqueFilters("caja", (caja) => caja || "-"),
			filterSearch: true,
			onFilter: (value, record) => (record.caja || "-") === value,
			sorter: (a, b) => (a.caja || "").localeCompare(b.caja || ""),
		},
		{
			title: "Turno",
			dataIndex: "turno",
			key: "turno",
			render: (turno) => {
				const turnoData = turnos?.find((t) => t.No === turno);
				return turnoData ? turnoData.DescripcionTurno : "-";
			},
			filters: getTurnoFilters(),
			filterSearch: true,
			onFilter: (value, record) => {
				const turnoData = turnos?.find((t) => t.No === record.turno);
				return turnoData ? turnoData.DescripcionTurno === value : false;
			},
			sorter: (a, b) => {
				const turnoA = turnos?.find((t) => t.No === a.turno);
				const turnoB = turnos?.find((t) => t.No === b.turno);
				return (turnoA?.DescripcionTurno || "").localeCompare(
					turnoB?.DescripcionTurno || ""
				);
			},
		},
		{
			title: "Cupón",
			dataIndex: "cupon",
			key: "cupon",
			render: (_, record) => {
				if (record.Cupon && record.Tipo_Detalle && record.No_Detalle) {
					return (
						<Tag color="green" icon={<TagOutlined />}>
							{record.Cupon} ({record.Tipo_Detalle}: {record.No_Detalle})
						</Tag>
					);
				}
				return "-";
			},
			filters: getUniqueFilters("Cupon"),
			filterSearch: true,
			onFilter: (value, record) => record.Cupon === value,
		},
		{
			title: "Campaña",
			dataIndex: "Campaign_No_",
			key: "Campaign_No_",
			render: (campaignNo) => {
				if (campaignNo) {
					return (
						<Tag color="blue" icon={<TagOutlined />}>
							{campaignNo}
						</Tag>
					);
				}
				return "-";
			},
			filters: getUniqueFilters("Campaign_No_"),
			filterSearch: true,
			onFilter: (value, record) => record.Campaign_No_ === value,
		},
		{
			title: "Estado",
			key: "syncStatus",
			render: (_, record) => {
				// Mostrar estado especial si se está sincronizando
				if (syncingInvoices.has(record.id) || record.syncing) {
					return <Tag color="processing">Sincronizando...</Tag>;
				}
				return getSyncStatusTag(record);
			},
			filters: getSyncStatusFilters(),
			filterSearch: true,
			onFilter: (value, record) => {
				switch (value) {
					case "draft":
						return record.isDraft;
					case "synced":
						return !!record.No && !record.deleted;
					case "pending":
						return !record.No && !record.deleted && !record.isDraft;
					case "deleted":
						return record.deleted;
					default:
						return true;
				}
			},
		},
		{
			title: "Acciones",
			key: "actions",
			render: (_, record) => (
				<Space size="small">
					<Button
						icon={<EyeOutlined />}
						size="small"
						onClick={() => handleViewDetail(record)}
						title="Ver detalle"
					/>
					<Button
						icon={<EditOutlined />}
						size="small"
						onClick={() => handleEdit(record)}
						// disabled={!record.isDraft}
						title={
							!record.isDraft
								? "Solo se pueden editar facturas en estado borrador"
								: "Editar factura"
						}
					/>
					<Button
						icon={<DeleteOutlined />}
						size="small"
						danger
						onClick={() => handleDelete(record)}
						disabled={!record.isDraft || record.deleted}
						title={
							!record.isDraft
								? "Solo se pueden eliminar facturas en estado borrador"
								: record.deleted
									? "Esta factura ya ha sido eliminada"
									: "Eliminar factura"
						}
					/>
					<Button
						icon={<SyncOutlined />}
						size="small"
						type="primary"
						onClick={() => handleSync(record)}
						loading={syncingInvoices.has(record.id) || record.syncing}
						disabled={
							!!record.No ||
							record.deleted ||
							record.isDraft ||
							syncingInvoices.has(record.id) ||
							record.syncing
						}
						title={
							syncingInvoices.has(record.id) || record.syncing
								? "Sincronizando..."
								: record.No
									? "La factura ya está sincronizada"
									: record.deleted
										? "Esta factura ha sido eliminada"
										: record.isDraft
											? "No se pueden sincronizar borradores"
											: "Sincronizar con Business Central"
						}
					/>
					{/* Solo mostrar el botón de crear nota de crédito en modo facturas */}
					{isInvoiceMode && (
						<Button
							icon={<RollbackOutlined />}
							size="small"
							type="dashed"
							onClick={() => handleCreateCreditMemo(record)}
							disabled={
								!record.No ||
								record.deleted ||
								record.Document_Type === "Credit Memo"
							}
							title={
								!record.No
									? "Solo se pueden crear notas de crédito de facturas sincronizadas"
									: record.deleted
										? "No se pueden crear notas de crédito de facturas eliminadas"
										: record.Document_Type === "Credit Memo"
											? "No se pueden crear notas de crédito de notas de crédito"
											: "Crear nota de crédito"
							}
						/>
					)}
					<Button
						icon={<PrinterOutlined />}
						size="small"
						onClick={() => handlePrintTicket(record)}
						title="Imprimir ticket"
					/>
					{isClientMissingForInvoice(record) && (
						<Button
							icon={<UserSwitchOutlined />}
							size="small"
							onClick={() => openReassignClientModal(record)}
							disabled={!!record.No || record.deleted}
							title={
								record.No
									? "No se puede reasignar cliente en facturas sincronizadas"
									: record.deleted
										? "Factura eliminada"
										: "Reasignar cliente"
							}
						/>
					)}
					{/* Botón para resetear estado de sincronización cuando esté atascada */}
					{(syncingInvoices.has(record.id) || record.syncing) && (
						<Button
							icon={<ReloadOutlined />}
							size="small"
							danger
							onClick={() => handleResetSyncStatus(record.id)}
							title="Resetear estado de sincronización"
						/>
					)}
				</Space>
			),
		},
	];

	// Calcular totales de la factura seleccionada
	const calculateInvoiceTotals = () => {
		if (!selectedInvoice || !selectedInvoice.lines)
			return { subtotal: 0, vat: 0, total: 0 };

		let subtotal = 0;
		let vat = 0;

		selectedInvoice.lines.forEach((line) => {
			const quantity = line.Quantity || 0;
			const unitPrice = line.Unit_Price || 0;
			const discount = line.Line_Discount_ || 0;
			const discountAmount = unitPrice * (discount / 100);
			const finalPrice = unitPrice - discountAmount;
			const lineSubtotal = quantity * finalPrice;

			// Aplicar IVA específico del producto
			const lineIva = line.PorIva || 0;
			const lineVat = lineSubtotal * (lineIva / 100);

			subtotal += lineSubtotal;
			vat += lineVat;
		});

		const total = subtotal + vat;
		return { subtotal, vat, total };
	};

	// Obtener el descuento máximo de las formas de pago
	const getPaymentMethodDiscount = () => {
		if (!paymentLines || paymentLines.length === 0) return 0;

		// Si hay más de una línea de pago, no aplicar descuento
		if (paymentLines.length > 1) return 0;

		// Solo si hay exactamente una línea de pago, aplicar su descuento
		return paymentLines[0].Dto || 0;
	};

	// Aplicar descuento de forma de pago al total
	const applyPaymentMethodDiscount = (amount) => {
		const discount = getPaymentMethodDiscount();
		if (!discount) return amount;

		return amount * (1 - discount / 100);
	};

	// Calcular total de pagos
	const calculatePaymentTotal = () => {
		if (!paymentLines) return 0;
		return paymentLines.reduce((sum, line) => sum + (line.Amount || 0), 0);
	};

	const { subtotal, vat, total: baseTotal } = calculateInvoiceTotals();
	const paymentMethodDiscount = getPaymentMethodDiscount();
	const total = applyPaymentMethodDiscount(baseTotal);
	const paymentTotal = calculatePaymentTotal();

	return (
		<div className="invoice-list-container">
			<div style={{ marginBottom: 16 }}>
				<h2 style={{ marginBottom: 16 }}>
					{isPendingMode
						? "Facturas pendientes de sincronizar"
						: isInvoiceMode
							? "Listado de facturas"
							: "Listado de abonos"}
				</h2>

				<Input
					placeholder="Buscar en todas las columnas..."
					prefix={<SearchOutlined />}
					value={searchText}
					onChange={(e) => setSearchText(e.target.value)}
					style={{ width: 300 }}
					allowClear
				/>
			</div>

			<Table
				columns={columns}
				dataSource={filteredInvoices}
				rowKey="id"
				loading={loading}
				pagination={{
					pageSize: 10,
					showSizeChanger: true,
					showQuickJumper: true,
					showTotal: (total, range) =>
						`${range[0]}-${range[1]} de ${total} elementos`,
				}}
				scroll={{ x: "max-content" }}
				size="small"
			/>

			{selectedInvoice && (
				<Drawer
					title={`Detalle de ${selectedInvoice.Document_Type === "Credit Memo"
						? "nota de crédito"
						: "factura"
						}: ${selectedInvoice.No || "Sin número"}`}
					placement="right"
					onClose={() => setDetailVisible(false)}
					open={detailVisible}
					width={700}
					className="invoice-detail-drawer"
				>
					<div>
						<h3>Información general</h3>
						<p>
							<strong>Tipo:</strong>{" "}
							{getInvoiceTypeTag(selectedInvoice.Document_Type)}
						</p>
						<p>
							<strong>
								Nº{" "}
								{selectedInvoice.Document_Type === "Credit Memo"
									? "Nota de crédito"
									: "Factura"}
								:
							</strong>{" "}
							{selectedInvoice.No || "-"}
						</p>

						{selectedInvoice.Document_Type === "Credit Memo" &&
							selectedInvoice.Applies_to_Doc_No && (
								<p className="reference-invoice">
									<strong>Factura corregida:</strong>{" "}
									{selectedInvoice.Applies_to_Doc_No}
								</p>
							)}

						<p>
							<strong>Cliente:</strong> {selectedInvoice.Sell_to_Customer_No_}
						</p>
						<p>
							<strong>Nombre:</strong> {selectedInvoice.Bill_to_Name}
						</p>
						<p>
							<strong>Dirección:</strong> {selectedInvoice.Bill_to_Address}
						</p>
						<p>
							<strong>Ciudad:</strong> {selectedInvoice.Bill_to_City}
						</p>
						<p>
							<strong>Número de cédula/RUC/Passaporte:</strong>{" "}
							{selectedInvoice.VAT_Registration_No_}
						</p>
						<p>
							<strong>País:</strong>{" "}
							{selectedInvoice.Bill_to_Country_Region_Code}
						</p>
						<p>
							<strong>Email:</strong> {selectedInvoice.E_Mail || ""}
						</p>
						<p>
							<strong>Teléfono:</strong> {selectedInvoice.Telefono || ""}
						</p>
						<p>
							<strong>Móvil:</strong> {selectedInvoice.Mobil || ""}
						</p>
						{colegio && (
							<p>
								<strong>Colegio:</strong> {colegio.No} - {colegio.nombre}
							</p>
						)}
						<p>
							<strong>Fecha:</strong>{" "}
							{selectedInvoice.Posting_Date
								? new Date(selectedInvoice.Posting_Date)
									.toISOString()
									.split("T")[0]
								: "-"}
						</p>
						<p>
							<strong>Caja:</strong> {selectedInvoice.caja || "-"}
						</p>
						<p>
							<strong>Turno:</strong> {selectedInvoice.turno || "-"}
						</p>
						{selectedInvoice.Cupon &&
							selectedInvoice.Tipo_Detalle &&
							selectedInvoice.No_Detalle && (
								<p>
									<strong>Cupón aplicado:</strong>{" "}
									<Tag color="green" icon={<TagOutlined />}>
										{selectedInvoice.Cupon}
									</Tag>
									<br />
									<small style={{ color: "#666" }}>
										<strong>Detalle:</strong> {selectedInvoice.Tipo_Detalle}:{" "}
										{selectedInvoice.No_Detalle}
									</small>
								</p>
							)}
						{selectedInvoice.Campaign_No_ && (
							<p>
								<strong>Campaña aplicada:</strong>{" "}
								<Tag color="blue" icon={<TagOutlined />}>
									{selectedInvoice.Campaign_No_}
								</Tag>
							</p>
						)}

						<h3 style={{ marginTop: "24px" }}>Líneas</h3>
						<Table
							columns={[
								{ title: "Nº", dataIndex: "No", key: "No" },
								{
									title: "Descripción",
									dataIndex: "Description",
									key: "Description",
								},
								{ title: "Cantidad", dataIndex: "Quantity", key: "Quantity" },
								{
									title: "Precio",
									dataIndex: "Unit_Price",
									key: "Unit_Price",
									render: (price) => `${price?.toFixed(2)} $`,
								},
								{
									title: "IVA",
									dataIndex: "PorIva",
									key: "PorIva",
									render: (iva) => `${(iva || 0).toFixed(2)}%`,
								},
								{
									title: "Dto.",
									dataIndex: "Line_Discount_",
									key: "Line_Discount_",
									render: (discount, record) => {
										if (!discount) return "-";

										const clientDiscount = record.Client_Discount_ || 0;
										const couponDiscount = record.Coupon_Discount_ || 0;

										return (
											<div
												style={{
													display: "flex",
													flexDirection: "column",
													gap: "2px",
												}}
											>
												<span style={{ fontWeight: "bold" }}>{discount}%</span>
												{clientDiscount > 0 && (
													<Tag color="blue" size="small">
														Cliente: {clientDiscount}%
													</Tag>
												)}
												{couponDiscount > 0 && (
													<Tag color="green" size="small">
														Cupón: {couponDiscount}%
													</Tag>
												)}
											</div>
										);
									},
								},
								{
									title: "Importe",
									key: "Amount",
									render: (_, record) => {
										const quantity = record.Quantity || 0;
										const unitPrice = record.Unit_Price || 0;
										const discount = record.Line_Discount_ || 0;
										const discountAmount = unitPrice * (discount / 100);
										const finalPrice = unitPrice - discountAmount;
										return `${(quantity * finalPrice).toFixed(2)} $`;
									},
								},
							]}
							dataSource={selectedInvoice.lines || []}
							pagination={false}
							scroll={{ x: "max-content" }}
						/>

						{selectedInvoice.lines && (
							<div className="invoice-totals" style={{ marginTop: "24px" }}>
								<div className="invoice-totals-item">
									<span>Subtotal:</span>
									<span
										className={
											selectedInvoice.Document_Type === "Credit Memo"
												? "negative-amount"
												: ""
										}
									>
										{subtotal.toFixed(2)} $
									</span>
								</div>
								<div className="invoice-totals-item">
									<span>IVA:</span>
									<span
										className={
											selectedInvoice.Document_Type === "Credit Memo"
												? "negative-amount"
												: ""
										}
									>
										{vat.toFixed(2)} $
									</span>
								</div>
								<div className="invoice-totals-item">
									<span>Total antes de descuentos:</span>
									<span
										className={
											selectedInvoice.Document_Type === "Credit Memo"
												? "negative-amount"
												: ""
										}
									>
										{baseTotal.toFixed(2)} $
									</span>
								</div>

								{paymentMethodDiscount > 0 && (
									<div className="invoice-totals-item">
										<span>
											Descuento forma de pago ({paymentMethodDiscount}%):
										</span>
										<span className="discount-amount">
											-{(baseTotal - total).toFixed(2)} $
										</span>
									</div>
								)}

								{paymentLines && paymentLines.length > 1 && (
									<div className="invoice-totals-item">
										<span>Descuento forma de pago:</span>
										<span style={{ color: "#ff4d4f" }}>
											No aplicable con múltiples formas de pago
										</span>
									</div>
								)}

								<div className="invoice-totals-item grand-total">
									<span>Total:</span>
									<span
										className={
											selectedInvoice.Document_Type === "Credit Memo"
												? "negative-amount"
												: ""
										}
									>
										{total.toFixed(2)} $
									</span>
								</div>

								{selectedInvoice.Document_Type === "Credit Memo" && (
									<div className="credit-memo-note">
										Esta nota de crédito representa una devolución o corrección
										de la factura indicada.
									</div>
								)}
							</div>
						)}

						{/* Sección de pagos */}
						{paymentLines && paymentLines.length > 0 && (
							<>
								<h3 style={{ marginTop: "24px" }}>Pagos</h3>
								<Table
									columns={[
										{
											title: "Forma de pago",
											dataIndex: "Payment_Method",
											key: "Payment_Method",
											render: (text, record) => (
												<span>
													{text}
													{record.Dto > 0 && (
														<Tag color="green" style={{ marginLeft: 8 }}>
															Dto: {record.Dto}%
														</Tag>
													)}
												</span>
											),
										},
										{
											title: "Importe",
											dataIndex: "Amount",
											key: "Amount",
											render: (amount) => `${amount?.toFixed(2)} $`,
										},
									]}
									dataSource={paymentLines}
									pagination={false}
									scroll={{ x: "max-content" }}
								/>

								<div className="invoice-totals" style={{ marginTop: "24px" }}>
									<div className="invoice-totals-item grand-total">
										<span>Total pagado:</span>
										<span>{paymentTotal.toFixed(2)} $</span>
									</div>
									<div className="invoice-totals-item grand-total">
										<span>Pendiente:</span>
										<span>{(total - paymentTotal).toFixed(2)} $</span>
									</div>
								</div>
							</>
						)}
					</div>
				</Drawer>
			)}

			{/* Modal de ticket para impresión */}
			{selectedInvoiceForTicket && (
				<InvoiceTicket
					open={ticketModalVisible}
					onClose={() => {
						setTicketModalVisible(false);
						setSelectedClientForTicket(null);
						setSelectedTPVForTicket(null);
					}}
					invoice={selectedInvoiceForTicket}
					lines={selectedInvoiceForTicket.lines || []}
					caja={selectedInvoiceForTicket.caja || "1"}
					turno={selectedInvoiceForTicket.turno || "1"}
					paymentLines={selectedInvoiceForTicket.paymentLines || []}
					selectedClient={selectedClientForTicket}
					selectedTPV={selectedTPVForTicket}
				/>
			)}

			<Modal
				open={reassignModalVisible}
				title="Reasignar cliente a factura"
				onCancel={() => {
					setReassignModalVisible(false);
					setInvoiceToReassign(null);
					setSelectedClientIdForReassign(null);
					setManualClientNoForReassign("");
				}}
				onOk={handleConfirmReassignClient}
				okText="Reasignar"
				cancelText="Cancelar"
			>
				<Tabs
					activeKey={reassignActiveTab}
					onChange={setReassignActiveTab}
					items={[
						{
							key: "select",
							label: "Seleccionar cliente",
							children: (
								<div>
									<p>Elija un cliente existente (si tiene Nº en BC, se usará ese Nº):</p>
									<Select
										showSearch
										placeholder="Seleccione cliente"
										style={{ width: "100%" }}
										value={selectedClientIdForReassign}
										onChange={(val) => setSelectedClientIdForReassign(val)}
										optionFilterProp="children"
										filterOption={(input, option) =>
											(option?.label || "").toLowerCase().includes(input.toLowerCase())
										}
									>
										{clients.map((c) => (
											<Select.Option
												key={c.id}
												value={c.id}
												label={`${c.No || c.id} - ${c.name}`}
											>
												{c.No || c.id} - {c.name}
												{!c.No && (
													<Tag color="orange" style={{ marginLeft: 8 }}>Sin Nº BC</Tag>
												)}
											</Select.Option>
										))}
									</Select>
								</div>
							),
						},
						{
							key: "manual",
							label: "Introducir Nº (BC)",
							children: (
								<div>
									<p>Escriba el Nº de cliente de Business Central:</p>
									<Input
										placeholder="Nº de cliente (BC)"
										value={manualClientNoForReassign}
										onChange={(e) => setManualClientNoForReassign(e.target.value)}
									/>
								</div>
							),
						},
						{
							key: "create",
							label: "Crear nuevo cliente",
							children: (
								<div>
									<p>Cree un cliente nuevo a partir de los datos de la factura.</p>
									<Divider />
									{(() => {
										console.log(invoiceToReassign);
										const initialData = invoiceToReassign
											? {
												name: invoiceToReassign.Bill_to_Name || "",
												Direccion: invoiceToReassign.Bill_to_Address || "",
												Direccion_2: invoiceToReassign.Direccion_2 || "",
												Poblacion: invoiceToReassign.Bill_to_City || "",
												Pais: invoiceToReassign.Bill_to_Country_Region_Code || "",
												Numero_Identificacion_fiscal: invoiceToReassign.VAT_Registration_No_ || "",
												Tipo_Documento_SrI: invoiceToReassign.Tipo_Documento_SrI || "Cedula",
												E_Mail: invoiceToReassign.E_Mail || "",
												Telefono: invoiceToReassign.Telefono || "",
												Mobil: invoiceToReassign.Mobil || "",
											}
											: {};
										return (
											<ClientForm
												initialData={initialData}
												skipAutoClearOnTipoChange={true}
												onClientAdded={(newClient) => {
													// Añadir el cliente a la lista local y seleccionarlo
													setClients((prev) => [...prev, newClient]);
													setSelectedClientIdForReassign(newClient.id);
													window.messageApi.success("Cliente creado. Ahora pulse Reasignar.");
												}}
												onClientUpdated={() => { }}
											/>
										);
									})()}
								</div>
							),
						},
					]}
				/>
			</Modal>
		</div>
	);
};

export default InvoiceList;
